import { useState, useEffect, useCallback } from 'react';
import { bffClient } from '@sluitstraat/core';
import { mapTypeWoning, mapAardBouw, mapAardDak } from './infofolio';
import { cleanUnselectedCoveragePremiums, formatCurrency } from './calc';
import { useDeferredCommit } from '../../../hooks/useDeferredCommit';
import { INBOEDEL_INFOFOLIO_FIELDS_KEY, INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY, infofolioAddressKey } from '../../../utils/glassPremium';

// Owns the Inboedel Infofolio machinery, moved verbatim from InboedelScreen: fetchPropertyInfo
// (including the IB_DAGWRDE -> IP_VERZSOM + IB_INBVST='M' derivation, its requestAnimationFrame
// DOM writes and the Woonhuis step-data sync), the monument flag, the infofolio-filled field
// locks, the pending-address trigger, and checkAddressChangedAndTrigger — the consolidated
// "address changed since last Infofolio lookup" check shared by the screen's loadSchema and its
// sharedAddress sync effect (loadSchema keeps its own data wipe, keyed on the returned flag).
//
// sessionStorage ownership: this hook owns 'inboedel_monument_property',
// 'inboedel_infofolio_fields', 'inboedel_infofolio_address' and 'inboedel_infofolio_lookup_done',
// plus the CROSS-PRODUCT write 'infofolio_BA_HERBWRD' (read by woonhuis/calc.ts
// linkInboedelAddress). The cross-product READ of 'woonhuis_infofolio_fields' stays in the
// screen's loadSchema. utils/addressSharing's syncAddressToOtherProducts clears
// INFOFOLIO_SESSION_KEYS externally — the hook tolerates keys vanishing (all sessionStorage
// reads are lazy). clearInfofolioTracking() is the one way to reset fields + address + done key
// in state AND storage together.
//
// MUST be called BEFORE useUpstreamSync (it consumes setInfofolioFilledFields and
// clearInfofolioTracking).
//
// fetchPropertyInfo lists every dependency it reads; the screen-side ones (saveWorkflowData, the
// setters, the deferred-commit pair) are stable, so the identity only moves with the data deps.

const SCREEN_ID = 'inboedel';

interface InfofolioArgs {
  selectedProducts: any;
  getStepData: (stepId: string) => any;
  saveStepData: (stepId: string, data: any) => void;
  saveWorkflowData: (stepId: string, displayData: any) => void;
  setData: (updater: any) => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export function useInfofolio({
  selectedProducts,
  getStepData,
  saveStepData,
  saveWorkflowData,
  setData,
  setValidationErrors,
}: InfofolioArgs) {
  // Track pending Infofolio lookup when address is copied from AVP (uses state to trigger useEffect)
  const [pendingInfofolioAddress, setPendingInfofolioAddress] = useState<{ postalCode: string; houseNumber: string; addition?: string } | null>(null);

  const { stage: stagePersist, schedule: schedulePersist } = useDeferredCommit();

  // Infofolio property info state
  const [_propertyInfo, setPropertyInfo] = useState<any>(null);
  const [isLoadingPropertyInfo, setIsLoadingPropertyInfo] = useState(false);

  // Monument property detection - monuments cannot be insured via SAA
  // Persist in sessionStorage to survive page navigation (cleared on full app reload in main.tsx)
  const [isMonumentProperty, setIsMonumentProperty] = useState(() => {
    try {
      const saved = sessionStorage.getItem('inboedel_monument_property');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });

  // Persist monument state to sessionStorage when it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('inboedel_monument_property', String(isMonumentProperty));
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }, [isMonumentProperty]);

  // Track which fields were filled by Infofolio (these become readonly)
  // Stored in sessionStorage to persist across navigation (separate from form data)
  const [infofolioFilledFields, setInfofolioFilledFields] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem(INBOEDEL_INFOFOLIO_FIELDS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const set = new Set<string>(parsed);
          // Migration: older sessions may have been saved before 'monumen' was tracked.
          // If Infofolio ran (any address-derived field present), 'monumen' is implied.
          if (set.size > 0) set.add('monumen');
          set.delete('permbew');
          return set;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return new Set();
  });

  // Track the address used for the last Infofolio lookup (to detect address changes)
  // Note: Value is read from sessionStorage during loadSchema, setter updates both state and sessionStorage
  const [_infofolioAddress, setInfofolioAddress] = useState<string>(() => {
    try {
      return sessionStorage.getItem('inboedel_infofolio_address') || '';
    } catch (e) {
      return '';
    }
  });

  // Address key of the last lookup that FINISHED (data, no data, or error — see the finally in
  // fetchPropertyInfo). Gates copy that may only appear once the lookup has run for the address
  // on screen (the Glas "contact DFD" note).
  const [infofolioLookupDoneFor, setInfofolioLookupDoneFor] = useState<string>(() => {
    try {
      return sessionStorage.getItem(INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY) || '';
    } catch (e) {
      return '';
    }
  });

  // Reset every piece of Infofolio tracking (fields, address, done key) in state and storage.
  // Shared with useUpstreamSync's no-valid-address branch so state and storage never disagree.
  const clearInfofolioTracking = useCallback(() => {
    try {
      sessionStorage.removeItem(INBOEDEL_INFOFOLIO_FIELDS_KEY);
      sessionStorage.removeItem('inboedel_infofolio_address');
      sessionStorage.removeItem(INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY);
    } catch (e) {
      // Ignore storage errors
    }
    setInfofolioFilledFields(new Set());
    setInfofolioAddress('');
    setInfofolioLookupDoneFor('');
  }, []);

  // Fetch property info from Infofolio after successful address validation
  const fetchPropertyInfo = useCallback(async (postalCode: string, houseNumber: string, addition?: string) => {
    try {
      setIsLoadingPropertyInfo(true);

      const response = await bffClient.getPropertyInfo(postalCode, houseNumber, addition || undefined);

      setPropertyInfo(response);

      // Extract property data from BFF response (nested under BA)
      const propertyData = response?.BA;

      if (propertyData) {
        // Map text values to form codes
        const mappedTypeWon = mapTypeWoning(propertyData.BA_TYPEWON);
        const mappedAardBw = mapAardBouw(propertyData.BA_AARDBW);
        // Use smart roof mapping with dakconstructie/dakbedekking from Infofolio
        const mappedAardDak = mapAardDak(
          propertyData.BA_AARDDAK,
          propertyData.BA_DAKCONS,      // dakconstructie (e.g., "Schuin", "Plat")
          propertyData.BA_DAKBED,       // dakbedekking (e.g., "Pannen", "Bitumen", "Riet")
          propertyData.BA_DAKBED_CODE   // dakbedekking_code (e.g., "60" = Riet, "70" = Kunstriet)
        );
        // Eigen woning from the Infofolio koop/huur flag: "Koop" -> 'J', anything else -> 'N'.
        const mappedEigenwo = propertyData.BA_EIGENWO
          ? (String(propertyData.BA_EIGENWO).toLowerCase().startsWith('koop') ? 'J' : 'N')
          : '';

        // Check monument status from Infofolio (BA_MONUMEN field)
        // Values: 'J'/'Y' = is monument, 'N' = not monument
        const monumentValue = propertyData.BA_MONUMEN;
        const isMonument = monumentValue === 'J' || monumentValue === 'Y';

        // Update monument state - monuments cannot be insured via SAA
        setIsMonumentProperty(isMonument);

        // Persist + downstream woonhuis sync. Staged from inside the updater, flushed
        // once AFTER the commit (useDeferredCommit): a setTimeout scheduled in the
        // updater would fire twice under StrictMode and even for discarded renders.
        const persistPropertyData = (dataToSave: any) => {
          const cleanedData = cleanUnselectedCoveragePremiums(dataToSave);
          saveWorkflowData(SCREEN_ID, cleanedData);

          // Sync to Woonhuis if selected - convert to numbers per AFD schema.
          // getStepData returns the LIVE context object: clone the written path so the
          // sync never mutates React state in place, and a partial doc (no BA yet)
          // cannot throw.
          if (selectedProducts?.woonhuis) {
            const stored = getStepData('woonhuis');
            if (stored) {
              const woonhuisData = {
                ...stored,
                Contractdocument: {
                  ...stored.Contractdocument,
                  PP: {
                    ...stored.Contractdocument?.PP,
                    BA: { ...stored.Contractdocument?.PP?.BA },
                    ...(stored.Contractdocument?.PP?.OP ? { OP: { ...stored.Contractdocument.PP.OP } } : {}),
                  },
                },
              };
              const ba = woonhuisData.Contractdocument.PP.BA;
              if (propertyData.BA_BOUWJR) ba.BA_BOUWJR = Number(propertyData.BA_BOUWJR);
              if (propertyData.BA_OPPWON) ba.BA_OPPWON = Number(propertyData.BA_OPPWON);
              if (mappedTypeWon) ba.BA_TYPEWON = mappedTypeWon;
              if (mappedAardBw) ba.BA_AARDBW = mappedAardBw;
              if (mappedAardDak) ba.BA_AARDDAK = mappedAardDak;
              if (mappedEigenwo) ba.BA_EIGENWO = mappedEigenwo;
              // Sync herbouwwaarde (rebuild value) to Woonhuis BA section and OP_VERZSOM
              if (propertyData.BA_HERBWRD) {
                const herbwrdValue = Number(propertyData.BA_HERBWRD);
                ba.BA_HERBWRD = herbwrdValue;
                ba.BA_HERBVST = 'M'; // Maatschappij - Infofolio determination
                if (!woonhuisData.Contractdocument.PP.OP) woonhuisData.Contractdocument.PP.OP = {};
                woonhuisData.Contractdocument.PP.OP.OP_VERZSOM = herbwrdValue;
              }
              // Sync new infofolio fields to Woonhuis
              if (propertyData.BA_BGM2) ba.BA_BGM2 = Number(propertyData.BA_BGM2);
              if (!ba.BA_PERMBEW) ba.BA_PERMBEW = 'J'; // prefill only, the customer's answer wins
              // Sync monument status to Woonhuis
              ba.BA_MONUMEN = isMonument ? 'J' : 'N';
              saveStepData('woonhuis', woonhuisData);
            }
          }
        };

        setData((prevData: any) => {
          const updatedData = { ...prevData };
          const fieldsUpdated: string[] = [];
          let hasChanges = false;

          // Ensure nested structure exists
          if (!updatedData.Contractdocument) updatedData.Contractdocument = {};
          if (!updatedData.Contractdocument.PP) updatedData.Contractdocument.PP = {};
          if (!updatedData.Contractdocument.PP.BA) updatedData.Contractdocument.PP.BA = {};
          if (!updatedData.Contractdocument.PP.IP) updatedData.Contractdocument.PP.IP = {};

          // Update bouwjaar (building year) - convert to number per AFD schema
          if (propertyData.BA_BOUWJR) {
            updatedData.Contractdocument.PP.BA.BA_BOUWJR = Number(propertyData.BA_BOUWJR);
            hasChanges = true;
            fieldsUpdated.push('bouwjaar');
          }

          // Update oppervlakte woning (property size) - convert to number per AFD schema
          if (propertyData.BA_OPPWON) {
            updatedData.Contractdocument.PP.BA.BA_OPPWON = Number(propertyData.BA_OPPWON);
            hasChanges = true;
            fieldsUpdated.push('oppwon');
          }

          // Update type woning (house type)
          if (mappedTypeWon) {
            updatedData.Contractdocument.PP.BA.BA_TYPEWON = mappedTypeWon;
            hasChanges = true;
            fieldsUpdated.push('typewon');
          }

          // Update aard bouw (wall construction)
          if (mappedAardBw) {
            updatedData.Contractdocument.PP.BA.BA_AARDBW = mappedAardBw;
            hasChanges = true;
            fieldsUpdated.push('aardbw');
          }

          // Update aard dak (roof type)
          if (mappedAardDak) {
            updatedData.Contractdocument.PP.BA.BA_AARDDAK = mappedAardDak;
            hasChanges = true;
            fieldsUpdated.push('aarddak');
          }

          // Update eigen woning (Koop/Huur from Infofolio)
          if (mappedEigenwo) {
            updatedData.Contractdocument.PP.BA.BA_EIGENWO = mappedEigenwo;
            hasChanges = true;
            fieldsUpdated.push('eigenwo');
          }

          // Store herbouwwaarde (rebuild value) - convert to number per AFD schema
          // Note: herbouwwaarde is NOT copied to OP_VERZSOM for inboedel.
          // OP is Opruimingskosten (clean-up costs), not the full building value.
          // Also persisted in sessionStorage for Woonhuis to pick up later.
          if (propertyData.BA_HERBWRD) {
            const herbwrdValue = Number(propertyData.BA_HERBWRD);
            updatedData.Contractdocument.PP.BA.BA_HERBWRD = herbwrdValue;
            sessionStorage.setItem('infofolio_BA_HERBWRD', String(herbwrdValue));
            hasChanges = true;
            fieldsUpdated.push('herbwrd');
          }

          // Update oppervlakte begane grond (ground floor area)
          if (propertyData.BA_BGM2) {
            updatedData.Contractdocument.PP.BA.BA_BGM2 = propertyData.BA_BGM2;
            hasChanges = true;
            fieldsUpdated.push('bgm2');
          }

          // Update totaaloppervlakte bijgebouwen - only when infofolio provides actual value
          if (propertyData.BA_TOPPBGB && Number(propertyData.BA_TOPPBGB) > 0) {
            updatedData.Contractdocument.PP.BA.BA_TOPPBGB = propertyData.BA_TOPPBGB;
            hasChanges = true;
            fieldsUpdated.push('toppbgb');
          }

          // Prefill permanent bewoond. NOT an Infofolio answer, so it is never locked and an
          // answer the customer already gave is kept.
          if (!updatedData.Contractdocument.PP.BA.BA_PERMBEW) updatedData.Contractdocument.PP.BA.BA_PERMBEW = 'J';
          hasChanges = true;

          // Update inboedelwaarde (contents value) from Infofolio IB_DAGWRDE -> IP_VERZSOM
          // IB_DAGWRDE is the contents day value indication from Infofolio
          // Also set IB_INBVST to "M" (Waardebepaling maatschappij) when the value is auto-filled via Infofolio
          if (propertyData.IB_DAGWRDE) {
            const inboedelValue = Number(propertyData.IB_DAGWRDE);
            if (inboedelValue > 0) {
              updatedData.Contractdocument.PP.IP.IP_VERZSOM = inboedelValue;
              // Auto-set IB_INBVST to "M" (Waardebepaling maatschappij - Infofolio determination)
              if (!updatedData.Contractdocument.PP.IB) updatedData.Contractdocument.PP.IB = {};
              updatedData.Contractdocument.PP.IB.IB_INBVST = 'M';
              // Derive IP_GARONDV, IP_TAXVRT, BA_HERBVST from IB_INBVST='M'
              updatedData.Contractdocument.PP.IP.IP_GARONDV = 'J'; // M -> J
              updatedData.Contractdocument.PP.IP.IP_TAXVRT = 'N';  // M is not V -> N
              updatedData.Contractdocument.PP.BA.BA_HERBVST = 'M'; // Pass through IB_INBVST value
              hasChanges = true;
              fieldsUpdated.push('inboedelwaarde');
              fieldsUpdated.push('inbvst');
            }
          }

          // Set monument status in data (already checked above)
          updatedData.Contractdocument.PP.BA.BA_MONUMEN = isMonument ? 'J' : 'N';
          if (isMonument) {
            hasChanges = true;
          }

          if (hasChanges) {
            stagePersist(() => persistPropertyData(updatedData));

            // Update DOM elements after React state update
            requestAnimationFrame(() => {
              // Update bouwjaar input
              if (propertyData.BA_BOUWJR) {
                const bouwjaarInput = document.getElementById('bouwjaar') as HTMLInputElement;
                if (bouwjaarInput) {
                  bouwjaarInput.value = String(propertyData.BA_BOUWJR);
                }
              }
              // Update inboedelwaarde/verzsom input
              if (propertyData.IB_DAGWRDE) {
                const verzomInput = document.getElementById('verzsom') as HTMLInputElement;
                if (verzomInput) {
                  verzomInput.value = formatCurrency(Number(propertyData.IB_DAGWRDE));
                }
              }
            });
          }

          return hasChanges ? updatedData : prevData;
        });
        schedulePersist();

        // Track which fields were filled by Infofolio (make them readonly)
        const filledFields = new Set<string>();
        if (propertyData.BA_BOUWJR) filledFields.add('bouwjaar');
        if (propertyData.BA_OPPWON) filledFields.add('oppwon');
        if (mappedTypeWon) filledFields.add('typewon');
        if (mappedAardBw) filledFields.add('aardbw');
        if (mappedAardDak) filledFields.add('aarddak');
        if (mappedEigenwo) filledFields.add('eigenwo');
        if (propertyData.IB_DAGWRDE && Number(propertyData.IB_DAGWRDE) > 0) {
          filledFields.add('inboedelwaarde');
          filledFields.add('inbvst'); // Also lock IB_INBVST when value comes from Infofolio
        }
        if (propertyData.BA_BGM2) filledFields.add('bgm2');
        if (propertyData.BA_TOPPBGB && Number(propertyData.BA_TOPPBGB) > 0) filledFields.add('toppbgb');
        if (propertyData.BA_HERBWRD) {
          filledFields.add('herbvst');
          filledFields.add('herbwrd');
        }
        // BA_MONUMEN is always set ('J' or 'N') after a successful Infofolio fetch
        filledFields.add('monumen');

        if (filledFields.size > 0) {
          setInfofolioFilledFields(filledFields);
          // Persist to sessionStorage so it survives navigation
          sessionStorage.setItem(INBOEDEL_INFOFOLIO_FIELDS_KEY, JSON.stringify(Array.from(filledFields)));

          // Store the address used for this Infofolio lookup (to detect address changes)
          const addressKey = infofolioAddressKey({ BA_PCODE: postalCode, BA_HUISNR: houseNumber, BA_TOEVOEG: addition });
          setInfofolioAddress(addressKey);
          sessionStorage.setItem('inboedel_infofolio_address', addressKey);

          // Clear validation errors for fields that were filled by Infofolio
          setValidationErrors(prev => {
            const updated = { ...prev };
            if (propertyData.BA_BOUWJR) delete updated['Contractdocument.PP.BA.BA_BOUWJR'];
            if (propertyData.BA_OPPWON) delete updated['Contractdocument.PP.BA.BA_OPPWON'];
            if (mappedTypeWon) delete updated['Contractdocument.PP.BA.BA_TYPEWON'];
            if (mappedAardBw) delete updated['Contractdocument.PP.BA.BA_AARDBW'];
            if (mappedAardDak) delete updated['Contractdocument.PP.BA.BA_AARDDAK'];
            if (mappedEigenwo) delete updated['Contractdocument.PP.BA.BA_EIGENWO'];
            if (propertyData.IB_DAGWRDE && Number(propertyData.IB_DAGWRDE) > 0) {
              delete updated['Contractdocument.PP.IP.IP_VERZSOM'];
              delete updated['Contractdocument.PP.IB.IB_INBVST']; // Also clear inbvst validation
            }
            if (propertyData.BA_HERBWRD) delete updated['Contractdocument.PP.BA.BA_HERBWRD'];
            delete updated['Contractdocument.PP.BA.BA_MONUMEN'];
            return updated;
          });
        }
      }
    } catch (error) {
      // Silently fail - Infofolio data is optional enhancement
    } finally {
      // Finished = answered for gating purposes (data, no data, or error): persisted like the
      // field set, so a remount that queues no new lookup still knows this address was looked up.
      const doneKey = infofolioAddressKey({ BA_PCODE: postalCode, BA_HUISNR: houseNumber, BA_TOEVOEG: addition });
      setInfofolioLookupDoneFor(doneKey);
      try { sessionStorage.setItem(INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY, doneKey); } catch (e) { /* ignore */ }
      setIsLoadingPropertyInfo(false);
    }
  }, [selectedProducts, getStepData, saveStepData, saveWorkflowData, stagePersist, schedulePersist, setData, setValidationErrors]);

  // Trigger Infofolio lookup when address was copied from AVP
  useEffect(() => {
    if (pendingInfofolioAddress) {
      const { postalCode, houseNumber, addition } = pendingInfofolioAddress;
      setPendingInfofolioAddress(null); // Clear pending so it only triggers once
      fetchPropertyInfo(postalCode, houseNumber, addition);
    }
  }, [pendingInfofolioAddress, fetchPropertyInfo]);

  // Consolidated "did the address change since the last Infofolio lookup?" check + trigger,
  // shared by the screen's loadSchema (init) and its sharedAddress sync effect (the latter only
  // while the risk address IS the policyholder address — a separate risk address must never be
  // looked up as the policyholder's). Compares the current address key against the stored
  // 'inboedel_infofolio_address'; on a change it clears the Infofolio tracking (fields + address
  // + done key, state + sessionStorage) and returns true so loadSchema can additionally wipe the
  // Infofolio-filled values from its local data (that wipe stays in the screen). Queues a fresh
  // lookup when there is a valid address and either no Infofolio data yet, the address changed,
  // or the done key belongs to another address. The done key has its OWN clear:
  // 'inboedel_infofolio_address' is only written when Infofolio returned data, so after a
  // no-data/error lookup addressHasChanged can never fire for it.
  // addressBA supplies BA_PCODE/BA_HUISNR/BA_TOEVOEG (local BA in loadSchema, sharedAddress in
  // the sync effect); ba/ip supply BA_TYPEWON/BA_HERBWRD/IP_VERZSOM for the has-data check
  // (ba defaults to addressBA — the loadSchema case).
  const checkAddressChangedAndTrigger = useCallback((addressBA: any, ip: any, ba: any = addressBA): boolean => {
    // Inboedel needs its own infofolio fetch for IB_DAGWRDE (inboedelwaarde), even when
    // building data is synced from Woonhuis. Only consider data complete when IP_VERZSOM is also set.
    const hasInfofolioData = (ba?.BA_TYPEWON || ba?.BA_HERBWRD) && ip?.IP_VERZSOM;
    const hasValidAddress = addressBA?.BA_PCODE && addressBA?.BA_HUISNR;

    // Create address key to compare with stored Infofolio address
    const currentAddressKey = infofolioAddressKey(addressBA);

    // Stored Infofolio address (last lookup WITH data) and done key (last lookup that finished)
    let storedInfofolioAddress = '';
    let storedLookupDone = '';
    try {
      storedInfofolioAddress = sessionStorage.getItem('inboedel_infofolio_address') || '';
      storedLookupDone = sessionStorage.getItem(INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY) || '';
    } catch (e) {
      // Ignore errors
    }

    // Determine if address has changed since last Infofolio lookup
    const addressHasChanged = Boolean(hasValidAddress && storedInfofolioAddress && currentAddressKey !== storedInfofolioAddress);
    // Done key left behind by a no-data/error lookup for another address
    const lookupDoneForOtherAddress = Boolean(storedLookupDone && storedLookupDone !== currentAddressKey);

    // Address changed: clear ALL Infofolio tracking (the form-data wipe stays in the screen's
    // loadSchema). Otherwise a stale done key is dropped on its own, so that wipe is not widened.
    if (addressHasChanged) {
      clearInfofolioTracking();
    } else if (lookupDoneForOtherAddress) {
      try { sessionStorage.removeItem(INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY); } catch (e) { /* ignore */ }
      setInfofolioLookupDoneFor('');
    }

    // Trigger Infofolio lookup if:
    // 1. No Infofolio data exists yet, OR
    // 2. Address has changed since last lookup, OR
    // 3. The last finished lookup was for another address (customer-typed data hides case 1)
    if (hasValidAddress && (!hasInfofolioData || addressHasChanged || lookupDoneForOtherAddress)) {
      // Use setTimeout to set state after the current render cycle completes
      setTimeout(() => {
        setPendingInfofolioAddress({
          postalCode: addressBA.BA_PCODE,
          houseNumber: addressBA.BA_HUISNR,
          addition: addressBA.BA_TOEVOEG || undefined,
        });
      }, 100);
    }

    return addressHasChanged;
  }, [clearInfofolioTracking]);

  return {
    isMonumentProperty,
    setIsMonumentProperty,
    isLoadingPropertyInfo,
    infofolioFilledFields,
    setInfofolioFilledFields,
    infofolioLookupDoneFor,
    clearInfofolioTracking,
    fetchPropertyInfo,
    checkAddressChangedAndTrigger,
  };
}
